import {
    Box,
    Typography,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Stack,
    Paper,
    Skeleton,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TuneIcon from '@mui/icons-material/Tune';
import HistoryIcon from '@mui/icons-material/History';
import ListEmptyState from './ListEmptyState';
import { queryInventoryHistory, queryEvents } from '../schema';
import { InventoryHistory } from '../schema';
import { filterValidInventoryHistory } from '../utils/instantDbGuards';
import { format, formatDistanceToNow } from 'date-fns';
import { useState, useMemo } from 'react';

interface Props {
    itemTypeId?: string;
    eventId?: string;
}

type TypeFilter = 'all' | 'purchase' | 'lost' | 'broken' | 'consumed' | 'losses' | 'adjustment';

export default function InventoryHistoryList({ itemTypeId, eventId }: Props) {
    const { data, isLoading, error } = queryInventoryHistory(itemTypeId);
    const { data: eventsData } = queryEvents();
    const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
    const [eventFilter, setEventFilter] = useState<string>('all');
    const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

    const events = useMemo(() => eventsData?.events || [], [eventsData?.events]);
    const allHistory = useMemo(
        () => filterValidInventoryHistory((data?.inventoryHistory || []) as InventoryHistory[]),
        [data?.inventoryHistory]
    );

    // Filter history
    const filteredHistory = useMemo(() => {
        let filtered = allHistory;

        // Filter by type
        if (typeFilter !== 'all') {
            if (typeFilter === 'losses') {
                filtered = filtered.filter(h => h.type === 'lost' || h.type === 'broken');
            } else {
                filtered = filtered.filter(h => h.type === typeFilter);
            }
        }

        // Filter by event
        if (eventFilter !== 'all') {
            if (eventFilter === 'hq') {
                filtered = filtered.filter(h => !h.event);
            } else {
                filtered = filtered.filter(h => h.event?.id === eventFilter);
            }
        }

        // Filter by itemType if provided
        if (itemTypeId) {
            filtered = filtered.filter(h => h.itemType?.id === itemTypeId);
        }

        // Filter by event if provided
        if (eventId) {
            filtered = filtered.filter(h => h.event?.id === eventId);
        }

        return filtered;
    }, [allHistory, typeFilter, eventFilter, itemTypeId, eventId]);

    // Get unique events from history for filter
    const availableEvents = useMemo(() => {
        const eventSet = new Set<string>();
        allHistory.forEach(h => {
            if (h.event?.id) {
                eventSet.add(h.event.id);
            }
        });
        const eventIds: string[] = [];
        for (const id of eventSet) eventIds.push(id);
        return eventIds.map(id => events.find(e => e.id === id)).filter((e): e is NonNullable<typeof e> => e !== undefined);
    }, [allHistory, events]);

    if (isLoading) {
        return (
            <Stack spacing={1}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: 1 }} />
                </Box>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: 1 }} />
                ))}
            </Stack>
        );
    }

    if (error) {
        return (
            <Typography variant="body2" color="error" sx={{ py: 2 }}>
                Error loading history: {error.message}
            </Typography>
        );
    }

    if (allHistory.length === 0) {
        return (
            <ListEmptyState
                icon={<HistoryIcon sx={{ fontSize: 48 }} />}
                message="No history yet"
                subtext="History of purchases, losses, and changes will appear here"
            />
        );
    }

    const getTypeConfig = (type: string) => {
        switch (type) {
            case 'purchase':
                return {
                    label: 'Purchase',
                    icon: <AddCircleIcon />,
                    color: 'success' as const,
                    bgColor: 'rgba(76, 175, 80, 0.1)',
                    format: (qty: number) => `+${qty} items`,
                };
            case 'lost':
                return {
                    label: 'Lost',
                    icon: <RemoveCircleIcon />,
                    color: 'error' as const,
                    bgColor: 'rgba(244, 67, 54, 0.1)',
                    format: (qty: number) => `-${Math.abs(qty)} items`,
                };
            case 'broken':
                return {
                    label: 'Broken',
                    icon: <BrokenImageIcon />,
                    color: 'error' as const,
                    bgColor: 'rgba(244, 67, 54, 0.1)',
                    format: (qty: number) => `-${Math.abs(qty)} items (broken)`,
                };
            case 'consumed':
                return {
                    label: 'Consumed',
                    icon: <LocalGasStationIcon />,
                    color: 'warning' as const,
                    bgColor: 'rgba(255, 152, 0, 0.1)',
                    format: (qty: number) => `-${Math.abs(qty)} items (consumed)`,
                };
            case 'adjustment':
                return {
                    label: 'Adjustment',
                    icon: <TuneIcon />,
                    color: 'info' as const,
                    bgColor: 'rgba(33, 150, 243, 0.1)',
                    format: (qty: number) => `${qty > 0 ? '+' : ''}${qty} items (adjusted)`,
                };
            default:
                return {
                    label: type,
                    icon: null,
                    color: 'default' as const,
                    bgColor: 'transparent',
                    format: (qty: number) => `${qty > 0 ? '+' : ''}${qty} items`,
                };
        }
    };

    const formatTimestamp = (timestamp: string) => {
        const date = new Date(timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

        if (daysDiff < 1) {
            return formatDistanceToNow(date, { addSuffix: true });
        } else if (daysDiff < 7) {
            return `${daysDiff} ${daysDiff === 1 ? 'day' : 'days'} ago`;
        } else {
            return format(date, 'MMM d, yyyy');
        }
    };

    const toggleNoteExpansion = (id: string) => {
        setExpandedNotes(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    return (
        <Box>
            {/* Filter Controls */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={typeFilter} label="Type" onChange={e => setTypeFilter(e.target.value as TypeFilter)}>
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="purchase">Purchases Only</MenuItem>
                        <MenuItem value="losses">Losses Only</MenuItem>
                        <MenuItem value="lost">Lost Only</MenuItem>
                        <MenuItem value="broken">Broken Only</MenuItem>
                        <MenuItem value="consumed">Consumption Only</MenuItem>
                        <MenuItem value="adjustment">Adjustments Only</MenuItem>
                    </Select>
                </FormControl>

                {!eventId && (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Event</InputLabel>
                        <Select value={eventFilter} label="Event" onChange={e => setEventFilter(e.target.value)}>
                            <MenuItem value="all">All Events</MenuItem>
                            <MenuItem value="hq">HQ</MenuItem>
                            {availableEvents.map(event => (
                                <MenuItem key={event.id} value={event.id}>
                                    {event.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Stack>

            {/* History Table */}
            {filteredHistory.length === 0 ? (
                <ListEmptyState
                    icon={<HistoryIcon sx={{ fontSize: 48 }} />}
                    message="No history matches the selected filters"
                    subtext="Try adjusting the type or event filters"
                />
            ) : (
                <Paper sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                {!itemTypeId && <TableCell>Item</TableCell>}
                                <TableCell>Quantity</TableCell>
                                {!eventId && <TableCell>Event</TableCell>}
                                <TableCell>Note</TableCell>
                                <TableCell>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredHistory.map(entry => {
                                const config = getTypeConfig(entry.type);
                                const isNoteExpanded = expandedNotes.has(entry.id);
                                const noteTruncated = entry.note && entry.note.length > 50;

                                return (
                                    <TableRow
                                        key={entry.id}
                                        sx={{
                                            bgcolor: config.bgColor,
                                            '&:hover': {
                                                bgcolor: config.bgColor.replace('0.1', '0.15'),
                                            },
                                        }}
                                    >
                                        <TableCell>
                                            <Chip
                                                {...(config.icon ? { icon: config.icon } : {})}
                                                label={config.label}
                                                size="small"
                                                color={config.color}
                                                sx={{ fontWeight: 'medium' }}
                                            />
                                        </TableCell>
                                        {!itemTypeId && (
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {entry.itemType?.photoUrl && (
                                                        <Box
                                                            component="img"
                                                            src={entry.itemType.photoUrl}
                                                            alt={entry.itemType.name}
                                                            sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 1 }}
                                                        />
                                                    )}
                                                    <Typography variant="body2">{entry.itemType?.name || 'Unknown'}</Typography>
                                                </Box>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 'medium',
                                                    color: entry.quantityChange > 0 ? 'success.main' : 'error.main',
                                                }}
                                            >
                                                {config.format(entry.quantityChange)}
                                            </Typography>
                                        </TableCell>
                                        {!eventId && (
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {entry.event
                                                        ? entry.event.name
                                                        : ((entry as { storageLocation?: { name?: string } | null }).storageLocation
                                                              ?.name ?? 'Storage')}
                                                </Typography>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            {entry.note ? (
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: isNoteExpanded || !noteTruncated ? 'block' : 'inline',
                                                            overflow: isNoteExpanded ? 'visible' : 'hidden',
                                                            textOverflow: isNoteExpanded ? 'clip' : 'ellipsis',
                                                            whiteSpace: isNoteExpanded ? 'normal' : 'nowrap',
                                                            maxWidth: noteTruncated && !isNoteExpanded ? '200px' : 'none',
                                                        }}
                                                    >
                                                        {entry.note}
                                                    </Typography>
                                                    {noteTruncated && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleNoteExpansion(entry.id)}
                                                            sx={{ ml: -1, mt: -0.5 }}
                                                        >
                                                            {isNoteExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                    No note
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatTimestamp(String(entry.timestamp))}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
}
